
import React, { useState } from 'react';
import { dbService } from '../services/dbService.ts';
import { SubscriptionTier, User, UserRole } from '../types.ts';

const MembershipView = ({ user, onChangeTab, activeTab = 'membership' }: { user: User, onChangeTab: (tab: string) => void, activeTab?: string }) => {
  const [currentTier, setCurrentTier] = useState(user.tier);
  const [isProcessing, setIsProcessing] = useState<string | null>(null);

  const isProfessional = user.role === UserRole.PROFESSIONAL;

  const plans = [
    { 
      id: SubscriptionTier.BASIC, 
      name: 'Basic', 
      price: 'Free', 
      features: [
        '3 Matches/mo', 
        'Radar View Discovery', 
        'Basic Sage AI Guidance', 
        'Profile Strength Score',
        'AI Career Strategist (Trial)'
      ] 
    },
    { 
      id: SubscriptionTier.STANDARD, 
      name: 'Standard', 
      price: '$14.99', 
      features: [
        '6 Matches/mo', 
        'Event Hub Access', 
        'Job Market Pulse',
        'Industry Specific Filters',
        'Priority Matching',
        'Auto Follow-up'
      ] 
    },
    { 
      id: SubscriptionTier.ADVANCED, 
      name: 'Advanced', 
      price: '$24.99', 
      features: [
        'Unlimited Matches', 
        'Priority Booking', 
        'AI Career Strategist', 
        'Reverse Mentorship Engine', 
        'Session Architect Prep',
        'Personalized Roadmap',
        'Post-Session Insights'
      ] 
    }
  ];

  const handleUpdate = (tier: SubscriptionTier) => {
    setIsProcessing(tier);
    setTimeout(() => {
      dbService.saveUser({ ...user, tier, updatedAt: Date.now() });
      setCurrentTier(tier);
      setIsProcessing(null);
      alert(`Membership Protocol Updated to ${tier}.`);
    }, 1200);
  };

  if (isProfessional) {
    return (
      <div className="p-12 lg:p-20 bg-slate-50/30 dark:bg-slate-950/30 min-h-screen transition-colors duration-500 overflow-y-auto font-sans flex flex-col items-center justify-center text-center">
         <div className="w-32 h-32 bg-blue-950 dark:bg-indigo-600 text-white rounded-[3rem] flex items-center justify-center text-6xl shadow-4xl mb-10 animate-in zoom-in duration-700">
            <i className="fas fa-certificate"></i>
         </div>
         <h1 className="text-5xl md:text-7xl font-black text-blue-950 dark:text-white tracking-tighter mb-6">Pro Status Active.</h1>
         <p className="text-xl text-slate-500 dark:text-slate-400 font-medium max-w-2xl leading-relaxed">
           As a verified professional mentor, you have complimentary access to all premium features of the CoffeeConnex ecosystem. Thank you for shaping the next generation.
         </p>
      </div>
    );
  }

  return (
    <div className="p-12 lg:p-20 bg-slate-50/30 dark:bg-slate-950/30 min-h-screen transition-colors duration-500 overflow-y-auto font-sans">
        <header className="mb-16 text-center max-w-4xl mx-auto">
          <span className="text-[10px] font-black uppercase tracking-[0.4em] text-indigo-600 dark:text-indigo-400 mb-3 block">Tier Calibration</span>
          <h1 className="text-6xl font-black text-blue-950 dark:text-white tracking-tighter">Membership.</h1>
          <p className="mt-4 text-slate-400 dark:text-slate-500 font-medium">Select a service level to scale your networking velocity.</p>
        </header>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 max-w-7xl mx-auto">
          {plans.map(plan => {
            const isActive = currentTier === plan.id;
            return (
              <div 
                key={plan.id} 
                className={`p-12 rounded-[4rem] transition-all duration-500 flex flex-col relative overflow-hidden ${
                  isActive 
                  ? 'bg-blue-950 dark:bg-indigo-600 text-white shadow-4xl scale-105 z-10' 
                  : 'bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 shadow-2xl text-slate-900 dark:text-white hover:border-indigo-500/30'
                }`}
              >
                {isActive && (
                  <div className="absolute top-0 right-0 p-8 opacity-10 text-[100px] pointer-events-none rotate-12"><i className="fas fa-crown"></i></div>
                )}
                
                <div className="mb-10">
                   <h3 className={`text-3xl font-black mb-2 tracking-tight ${isActive ? 'text-white' : 'text-blue-950 dark:text-white'}`}>{plan.name}</h3>
                   <p className={`text-5xl font-black tracking-tighter ${isActive ? 'text-indigo-200' : 'text-indigo-600 dark:text-indigo-400'}`}>{plan.price}</p>
                </div>

                <ul className="space-y-5 mb-12 flex-1">
                  {plan.features.map((f, i) => (
                    <li key={i} className={`text-sm font-bold flex items-center gap-3 ${isActive ? 'text-white/80' : 'text-slate-500 dark:text-slate-400'}`}>
                      <div className={`w-5 h-5 rounded-full flex items-center justify-center text-[8px] shrink-0 ${isActive ? 'bg-white/20' : 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400'}`}>
                        <i className="fas fa-check"></i>
                      </div>
                      {f}
                    </li>
                  ))}
                </ul>

                <button 
                  onClick={() => handleUpdate(plan.id as SubscriptionTier)} 
                  disabled={isActive || !!isProcessing} 
                  className={`w-full py-5 rounded-[1.8rem] font-black text-xs uppercase tracking-widest transition-all active:scale-95 disabled:opacity-50 ${
                    isActive 
                    ? 'bg-white text-blue-950 shadow-xl' 
                    : 'bg-blue-950 dark:bg-slate-800 text-white hover:bg-blue-900 dark:hover:bg-slate-700'
                  }`}
                >
                  {isProcessing === plan.id ? <i className="fas fa-circle-notch animate-spin"></i> : isActive ? 'Current Tier Active' : 'Select Plan'}
                </button>
              </div>
            );
          })}
        </div>
    </div>
  );
};

export default MembershipView;
